import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Button,
  Alert,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { serverHost, createEndpoints } from "variables/serverdata";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { Input } from "reactstrap";

  // ── Destination dropdown (shared) ──────────────────────────────────────────
  const DestinationSelect = ({ value, onChange, style, size }) => (
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={style}
      bsSize={size}
      placeholder="Enter destination..."
    />
  );

  // ── Toggle helper ──────────────────────────────────────────────────────────
  const Toggle = ({ checked, onChange, id: toggleId }) => (
    <label className="custom-toggle mb-0">
      <input
        type="checkbox"
        id={toggleId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        className="custom-toggle-slider rounded-circle"
        data-label-off="Off"
        data-label-on="On"
      />
    </label>
  );

const CallForwardAddEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
  const site_uuid = currentSite?.site_uuid || null;
  const [loading, setLoading] = useState(false);

  // ── Call Forward section ───────────────────────────────────────────────────
  // Each forward type: { enabled, destination }
  const [callForward, setCallForward] = useState({
    all: { enabled: false, destination: "" },
    busy: { enabled: false, destination: "" },
    no_answer: { enabled: false, destination: "" },
    not_registered: { enabled: false, destination: "" },
  });

  const callForwardLabels = {
    all: "Call Forward",
    busy: "On Busy",
    no_answer: "No Answer",
    not_registered: "Not Registered",
  };

  // ── Follow Me section ──────────────────────────────────────────────────────
  const [followMeEnabled, setFollowMeEnabled] = useState(false);
  const [followMeDestinations, setFollowMeDestinations] = useState([
    { destination: "", delay: "0", timeout: "30", prompt: false, order: 0 },
  ]);

  // ── Simple toggles ─────────────────────────────────────────────────────────
  const [ignoreBusy, setIgnoreBusy] = useState(false);
  const [dnd, setDnd] = useState(false);
  const [ext, setExt] = useState("")
  // ── Fetch data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchExisting = async () => {
      if (!id) return;
      try {
        const res = await fetch(
          `${serverHost}/fetch_forward/${encodeURIComponent(id)}`,
        );
        if (!res.ok) throw new Error("Failed to fetch call forward data");
        const data = await res.json();
        if (!data?.data) throw new Error("Row not found");
        const route_data = data.data
        console.log(route_data)
        const cf = route_data.call_forward;
        const fm = route_data.follow_me;
        const fmd = route_data.destinations
        setExt(cf.extension)
        setCallForward({
          all: {
            enabled: cf.forward_all_enabled === "true",
            destination: cf.forward_all_destination || "",
          },
          busy: {
            enabled: cf.forward_busy_enabled === "true",
            destination: cf.forward_busy_destination || "",
          },
          no_answer: {
            enabled: cf.forward_no_answer_enabled === "true",
            destination: cf.forward_no_answer_destination || "",
          },
          not_registered: {
            enabled: cf.forward_not_registered_enabled === "true",
            destination: cf.forward_not_registered_destination || "",
          },
        });
        if (fm) {
          setFollowMeEnabled(fm.follow_me_enabled === "true");
          setIgnoreBusy(fm.follow_me_ignore_busy === "true");
          setDnd(fm.do_not_disturb === "true");
        }
        if (fmd) {
          setFollowMeDestinations(fmd);
        }
      } catch (err) {
        console.error("Error fetching call forward data:", err);
        setError(`Failed to load existing data: ${err}`);
      }
    };

    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchExisting()]);
      } catch (err) {
        console.error("Load error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleCallForwardToggle = (type, value) => {
    setCallForward((prev) => ({
      ...prev,
      [type]: { ...prev[type], enabled: value },
    }));
  };

  const handleCallForwardDestination = (type, value) => {
    setCallForward((prev) => ({
      ...prev,
      [type]: { ...prev[type], destination: value },
    }));
  };
  
  const addFollowMeDestination = () => {
    setFollowMeDestinations((prev) => [
      ...prev,
      { destination: "", delay: "0", timeout: "30", prompt: false, order: prev.length > 0 ? parseInt(prev[prev.length - 1].order) + 1 : 1 },
    ]);
  };;

  const removeFollowMeDestination = (index) => {
    setFollowMeDestinations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFollowMeDestChange = (index, field, value) => {
    setFollowMeDestinations((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const site = JSON.parse(localStorage.getItem("selectedSite"));
      const payload = {
        call_forward: callForward,
        follow_me_enabled: followMeEnabled,
        follow_me_destinations: followMeDestinations,
        follow_me_ignore_busy: ignoreBusy,
        do_not_disturb: dnd,
        client_uuid: site.client_uuid,
        site_uuid: site.site_uuid,
        insert_uuid: JSON.parse(localStorage.getItem("user")).user_uuid,
      };

      let url = `${serverHost}/modify_call_forward/${id}`;

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        navigate("/admin/hucs/call-forward");
      } else {
        const err = await response.json();
        console.error("Failed to save call forward:", err);
        setError("Failed to save. Please try again.");
      }
    } catch (err) {
      console.error("Error saving call forward:", err);
      setError("An unexpected error occurred.");
    }
  };

  const handleCancel = () => navigate("/admin/hucs/call-forward");

  // ── Styles ─────────────────────────────────────────────────────────────────
  const inputStyle = {
    backgroundColor: isDark ? "#374151" : "#fff",
    color: isDark ? "#e2e8f0" : "#495057",
    borderColor: isDark ? "#4b5563" : "#ced4da",
  };

  const labelStyle = {
    color: isDark ? "#e2e8f0" : "#495057",
    fontWeight: 500,
  };

  const sectionStyle = {
    backgroundColor: isDark ? "#1f2937" : "#fff",
    border: `1px solid ${isDark ? "#374151" : "#dee2e6"}`,
    borderRadius: "0.375rem",
    padding: "1.25rem",
    marginBottom: "1.25rem",
  };

  const sectionHeaderStyle = {
    color: isDark ? "#93c5fd" : "#1e3a5f",
    fontWeight: 700,
    fontSize: "0.8rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: "1rem",
    paddingBottom: "0.5rem",
    borderBottom: `1px solid ${isDark ? "#374151" : "#dee2e6"}`,
  };

  if (loading) {
    return (
      <>
        <SimpleHeader name="Call Forward" parentName="HUCS" />
        <Container className="mt--6">
          <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
            <Card.Body className="p-4 text-center">
              <p>
                Loading<span className="loading-dots"></span>
              </p>
            </Card.Body>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <SimpleHeader
        name={`${id ? "EDIT" : "ADD"} Call Forward`}
        parentName="HUCS"
      />
      <Container fluid className="mt--6">
        <Card className="shadow-sm" style={{ border: "none" }}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#e9ecef",
              color: isDark ? "#e2e8f0" : "#1e3a5f",
              borderBottom: "none",
              padding: "1rem 1.5rem",
            }}
          >
            <h5 className="mb-0" style={{ fontWeight: 600 }}>
               CALL FORWARD for {ext}
            </h5>
          </Card.Header>

          <Card.Body
            style={{
              backgroundColor: isDark ? "#111827" : "#f3f4f6",
              padding: "1.5rem",
            }}
          >
            {error && <Alert variant="danger">{error}</Alert>}

            <Form onSubmit={handleSubmit}>
              {/* ── CALL FORWARD SECTION ──────────────────────────────────── */}
              <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>Call Forward</div>

                {Object.entries(callForwardLabels).map(([type, label]) => (
                  <Row key={type} className="mb-3 align-items-end">
                    <Col md={2} className="d-flex align-items-end pb-1">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                          paddingBottom: "0.25rem",
                        }}
                      >
                        <span
                          style={{
                            ...labelStyle,
                            fontWeight: 600,
                            minWidth: "90px",
                          }}
                        >
                          {label}
                        </span>
                        <Toggle
                          checked={callForward[type].enabled}
                          onChange={(val) => handleCallForwardToggle(type, val)}
                          id={`cf-toggle-${type}`}
                        />
                      </div>
                    </Col>
                    <Col md={10}>
                      <Form.Label
                        style={{ ...labelStyle, fontSize: "0.85rem" }}
                      >
                        Destination
                      </Form.Label>
                      <DestinationSelect
                        value={callForward[type].destination}
                        onChange={(val) =>
                          handleCallForwardDestination(type, val)
                        }
                        style={inputStyle}
                      />
                    </Col>
                  </Row>
                ))}
              </div>

              {/* ── FOLLOW ME SECTION ─────────────────────────────────────── */}
              <div style={sectionStyle}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    ...sectionHeaderStyle,
                  }}
                >
                  <span>Follow Me</span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                    }}
                  >
                    <span
                      style={{
                        ...labelStyle,
                        fontSize: "0.85rem",
                        textTransform: "none",
                        letterSpacing: 0,
                      }}
                    >
                      {followMeEnabled ? "Enabled" : "Disabled"}
                    </span>
                    <Toggle
                      checked={followMeEnabled}
                      onChange={setFollowMeEnabled}
                      id="follow-me-toggle"
                    />
                  </div>
                </div>

                {/* Follow Me Destinations */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label
                      style={{
                        ...labelStyle,
                        marginBottom: 0,
                        fontSize: "0.875rem",
                      }}
                    >
                      Destinations
                    </Form.Label>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={addFollowMeDestination}
                      style={{ borderColor: "#5469d4", color: "#5469d4" }}
                    >
                      + Add Destination
                    </Button>
                  </div>

                  {followMeDestinations.map((dest, index) => (
                    <Card
                      key={index}
                      className="mb-2"
                      style={{
                        backgroundColor: isDark ? "#374151" : "#f8f9fa",
                        border: `1px solid ${isDark ? "#4b5563" : "#dee2e6"}`,
                      }}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small
                            style={{
                              color: isDark ? "#9ca3af" : "#6c757d",
                              fontWeight: 600,
                            }}
                          >
                            Destination {index + 1}
                          </small>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={() => removeFollowMeDestination(index)}
                          >
                            Remove
                          </Button>
                        </div>

                        <Row>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label
                                style={{ ...labelStyle, fontSize: "0.875rem" }}
                              >
                                Destination
                              </Form.Label>
                              <DestinationSelect
                                value={dest.destination}
                                onChange={(val) =>
                                  handleFollowMeDestChange(
                                    index,
                                    "destination",
                                    val,
                                  )
                                }
                                style={inputStyle}
                              />
                            </Form.Group>
                          </Col>

                          <Col md={2}>
                            <Form.Group>
                              <Form.Label
                                style={{ ...labelStyle, fontSize: "0.875rem" }}
                              >
                                Delay (sec)
                              </Form.Label>
                              <Form.Control
                                type="number"
                                size="sm"
                                min="0"
                                value={dest.delay}
                                onChange={(e) =>
                                  handleFollowMeDestChange(
                                    index,
                                    "delay",
                                    e.target.value,
                                  )
                                }
                                placeholder="0"
                                style={inputStyle}
                              />
                            </Form.Group>
                          </Col>

                          <Col md={2}>
                            <Form.Group>
                              <Form.Label
                                style={{ ...labelStyle, fontSize: "0.875rem" }}
                              >
                                Timeout (sec)
                              </Form.Label>
                              <Form.Control
                                type="number"
                                size="sm"
                                min="1"
                                value={dest.timeout}
                                onChange={(e) =>
                                  handleFollowMeDestChange(
                                    index,
                                    "timeout",
                                    e.target.value,
                                  )
                                }
                                placeholder="30"
                                style={inputStyle}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Form.Group>
                              <Form.Label
                                style={{ ...labelStyle, fontSize: "0.875rem" }}
                              >
                                Order
                              </Form.Label>
                              <Form.Control
                                type="number"
                                size="sm"
                                min="0"
                                value={dest.order}
                                onChange={(e) =>
                                  handleFollowMeDestChange(
                                    index,
                                    "order",
                                    e.target.value,
                                  )
                                }
                                placeholder="0"
                                style={inputStyle}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2} className="d-flex align-items-end">
                            <Form.Group>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.75rem",
                                  paddingBottom: "2rem",
                                }}
                              >
                                <span
                                  style={{
                                    color: isDark ? "#e2e8f0" : "#495057",
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  Prompt
                                </span>
                                <Toggle
                                  checked={dest.prompt === "true"}
                                  onChange={(val) =>
                                    handleFollowMeDestChange(
                                      index,
                                      "prompt",
                                      val,
                                    )
                                  }
                                  id={`fm-prompt-${index}`}
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </div>

              {/* ── IGNORE BUSY + DND ─────────────────────────────────────── */}
              <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>Additional Settings</div>
                <Row>
                  <Col md={6}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                      }}
                    >
                      <span style={{ ...labelStyle, fontWeight: 600 }}>
                        Ignore Busy
                      </span>
                      <Toggle
                        checked={ignoreBusy}
                        onChange={setIgnoreBusy}
                        id="ignore-busy-toggle"
                      />
                    </div>
                    <small
                      style={{
                        color: isDark ? "#6b7280" : "#9ca3af",
                        display: "block",
                        marginTop: "0.25rem",
                      }}
                    >
                      Continue ringing Follow Me destinations even when the
                      extension is busy
                    </small>
                  </Col>

                  <Col md={6}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                      }}
                    >
                      <span style={{ ...labelStyle, fontWeight: 600 }}>
                        Do Not Disturb (DND)
                      </span>
                      <Toggle checked={dnd} onChange={setDnd} id="dnd-toggle" />
                    </div>
                    <small
                      style={{
                        color: isDark ? "#6b7280" : "#9ca3af",
                        display: "block",
                        marginTop: "0.25rem",
                      }}
                    >
                      Send all calls directly to voicemail
                    </small>
                  </Col>
                </Row>
              </div>

              {/* ── ACTION BUTTONS ────────────────────────────────────────── */}
              <div className="d-flex justify-content-between mt-4">
                <Button variant="danger" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant="success" type="submit">
                  {`${id ? "Save Changes" : "Add Call Forward"}`}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default CallForwardAddEdit;
